import React, { useState } from 'react';
import { Table, Spinner, Form, Row, Col, Button, Alert } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Card, CardBody, CardFooter, CardHeader } from '@components/card/card';
import type { APIResponse } from '@store/Shared/shared';
import { ntxAPI } from '@API/axiosNeatex';

interface AccountStatement {
  id: string;
  jE_CATEGORY: string;
  jE_CATEGORY_NAME: string;
  jE_CATEGORY_NAME2: string;
  jE_SOURCE: string;
  jE_SOURCE_NAME: string;
  jE_SOURCE_NAME2: string;
  perioD_ID: string;
  perioD_NAME: string;
  perioD_NAME2: string;
  gL_DATE: string;
  amounT_DR: number | null;
  amounT_CR: number | null;
  jE_LINE_DESCRIPTION: string;
  codE_COMBINATION_CODE: string;
  account: string;
  accounT_NAME: string;
  accounT_NAME2: string;
  accounT_NUMBER: string;
  accounT_PARANT: string;
  accounT_TYPE: string;
  accounT_LEVEL: number;
}

const AccountStatementReport: React.FC = () => {
  const [data, setData] = useState<AccountStatement[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [accountNumber, setAccountNumber] = useState<string>('');
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [startDate, endDate] = dateRange;
  const [category, setCategory] = useState<string>('all');

  // Function to fetch data with filters
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Prepare query parameters

      const { data } = await ntxAPI.get<APIResponse<AccountStatement[]>>('Reports/GetAccountStatement', {
        params: { ACCOUNT_NUMBER: accountNumber, StartDate: startDate, EndDate: endDate }
      });
      if (data.succeeded === true) {
        setData(data.data);
      } else {
        setError(data instanceof Error ? data.message : 'An unknown error occurred');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Get unique categories for dropdown
  const getUniqueCategories = () => {
    const categories = data.map(item => ({
      id: item.jE_CATEGORY,
      name: item.jE_CATEGORY_NAME
    }));

    return Array.from(new Map(categories.map(item => [item.id, item])).values());
  };

  const resetFilters = () => {
    setAccountNumber('');
    setDateRange([null, null]);
    setCategory('all');
  };

  const formatCurrency = (amount: number | null): string => {
    if (amount === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString();
  };

  // Calculate balance for each entry
  const calculateBalance = (dr: number | null, cr: number | null) => {
    const debit = dr || 0;
    const credit = cr || 0;
    return debit - credit;
  };

  return (
    <div className='container mt-4'>
      <Card>
        <CardHeader className='d-flex justify-content-between align-items-center'>
          <h5 className='mb-0'>Account Statement Report</h5>
          {data.length > 0 && (
            <div>
              <span className='me-2'>Showing: {data.length} entries</span>
            </div>
          )}
        </CardHeader>

        <CardBody>
          {error && (
            <Alert variant='danger' className='mb-4'>
              Error loading data: {error}
            </Alert>
          )}

          {/* Filters Section */}
          <Card className='mb-4'>
            <CardHeader>Filters</CardHeader>
            <CardBody>
              <Form>
                <Row className='g-3'>
                  <Col md={3}>
                    <Form.Group controlId='accountFilter'>
                      <Form.Label>Account Number</Form.Label>
                      <Form.Control
                        type='text'
                        placeholder='Enter account number'
                        value={accountNumber}
                        onChange={e => setAccountNumber(e.target.value)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group controlId='dateRangeFilter'>
                      <Form.Label>Date Range</Form.Label>
                      <DatePicker
                        selectsRange={true}
                        startDate={startDate}
                        endDate={endDate}
                        onChange={update => setDateRange(update)}
                        isClearable={true}
                        className='form-control'
                        placeholderText='Select date range'
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group controlId='categoryFilter'>
                      <Form.Label>Journal Category</Form.Label>
                      <Form.Select value={category} onChange={e => setCategory(e.target.value)}>
                        <option value='all'>All Categories</option>
                        {getUniqueCategories().map((cat, index) => (
                          <option key={`${cat.id}-${index}`} value={cat.id}>
                            {cat.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col xs={12} className='text-end'>
                    <Button variant='secondary' onClick={resetFilters} className='me-2'>
                      Reset Filters
                    </Button>
                    <Button variant='primary' onClick={fetchData} disabled={loading}>
                      {loading ? (
                        <>
                          <Spinner
                            as='span'
                            animation='border'
                            size='sm'
                            role='status'
                            aria-hidden='true'
                            className='me-2'
                          />
                          Loading...
                        </>
                      ) : (
                        'Load Report'
                      )}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>

          {/* Results Section */}
          {loading ? (
            <div className='text-center my-5'>
              <Spinner animation='border' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </Spinner>
              <p className='mt-2'>Loading account statement data...</p>
            </div>
          ) : data.length > 0 ? (
            <div className='table-responsive'>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Account</th>
                    <th>Account #</th>
                    <th>Category</th>
                    <th>Source</th>
                    <th>Description</th>
                    <th>Debit</th>
                    <th>Credit</th>
                    <th>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, index) => {
                    const balance = calculateBalance(item.amounT_DR, item.amounT_CR);
                    return (
                      <tr key={`${item.id}-${index}`}>
                        <td>{formatDate(item.gL_DATE)}</td>
                        <td>
                          <div>{item.accounT_NAME}</div>
                          {item.accounT_NAME2 && <div className='text-muted small'>{item.accounT_NAME2}</div>}
                        </td>
                        <td>{item.accounT_NUMBER}</td>
                        <td>
                          <div>{item.jE_CATEGORY_NAME}</div>
                          {item.jE_CATEGORY_NAME2 && <div className='text-muted small'>{item.jE_CATEGORY_NAME2}</div>}
                        </td>
                        <td>
                          <div>{item.jE_SOURCE_NAME}</div>
                          {item.jE_SOURCE_NAME2 && <div className='text-muted small'>{item.jE_SOURCE_NAME2}</div>}
                        </td>
                        <td>{item.jE_LINE_DESCRIPTION || '-'}</td>
                        <td className='text-danger'>{formatCurrency(item.amounT_DR)}</td>
                        <td className='text-success'>{formatCurrency(item.amounT_CR)}</td>
                        <td className={balance > 0 ? 'text-danger' : balance < 0 ? 'text-success' : ''}>
                          {formatCurrency(Math.abs(balance))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className='text-center py-5'>
              <p>No data available. Set your filters and click "Load Report" to generate.</p>
            </div>
          )}
        </CardBody>

        {data.length > 0 && (
          <CardFooter className='text-muted'>Report generated on {new Date().toLocaleString()}</CardFooter>
        )}
      </Card>
    </div>
  );
};

export default AccountStatementReport;
